import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import AccountIcon from "../Icons/AccountIcon";
import { Modal, ModalHeader, ModalBody } from "../Modal";
import Badge from "../Badge";
import { ClientProfile } from "../../interfaces/common";
import { CoachInterface } from "../../interfaces/common";
import { getLanguage } from "../../lang/helpers";

interface Props {
  clientProfile: ClientProfile;
  coach: CoachInterface;
}

const CoachMatching: React.FC<Props> = ({ clientProfile, coach }) => {
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [showMore, setShowMore] = useState<string | null>(null);

  const showMoreData = () => {
    const name: string = showMore ? showMore : "unknown";
    let header = name;
    let matched: Array<string> = [];
    let coachArr: Array<string> = [];
    let clientArr: Array<string> = [];
    let clientAreas = 0;
    if (showMore === "areas") {
      const coachKeys = [];
      for (const area of coach.profile.coachingAreas) {
        if (clientProfile.coachingAreas.includes(area.key)) {
          matched.push(t(area.key));
        } else {
          coachArr.push(t(area.key));
        }
        coachKeys.push(area.key);
      }

      for (const area of clientProfile.coachingAreas) {
        if (!coachKeys.includes(area)) {
          clientArr.push(t(area));
        }
      }
      clientAreas = clientProfile.coachingAreas.length;
      header = lang === "sv" ? "Coachområden" : "Coaching areas";
    } else if (showMore === "client_industries") {
      for (const area of coach.profile.clientIndustries) {
        if (clientProfile.coachIndustryExperiences.includes(area)) {
          matched.push(t(area));
        } else {
          coachArr.push(t(area));
        }
      }
      for (const area of clientProfile.coachIndustryExperiences) {
        if (!coach.profile.clientIndustries.includes(area)) {
          clientArr.push(t(area));
        }
      }
      clientAreas = clientProfile.coachIndustryExperiences.length;
      header = lang === "sv" ? "Brancher" : "Industries";
    } else if (showMore === "coach_industries") {
      for (const area of coach.profile.industries) {
        if (clientProfile.coachIndustryExperiences.includes(area)) {
          matched.push(t(area));
        } else {
          coachArr.push(t(area));
        }
      }
      for (const area of clientProfile.coachIndustryExperiences) {
        if (!coach.profile.industries.includes(area)) {
          clientArr.push(t(area));
        }
      }
      clientAreas = clientProfile.coachIndustryExperiences.length;
      header =
        lang === "sv"
          ? "Coachens branscherfarenhet"
          : "Coach industry experience";
    } else if (showMore === "languages") {
      for (const area of coach.profile.languages) {
        if (clientProfile.languagesProfessional.includes(area) || clientProfile.languagesNative.includes(area)) {
          matched.push(t("language_" + area));
        } else {
          coachArr.push(t("language_" + area));
        }
      }
      for (const area of clientProfile.languagesProfessional) {
        if (!coach.profile.languages.includes(area)) {
          clientArr.push(t("language_" + area));
        }
      }
      clientAreas = clientProfile.languagesProfessional.length;
      header = t("languages");
    }
    return (
      <Modal isOpen={true} onClose={() => setShowMore(null)}>
        <ModalHeader onClose={() => setShowMore(null)}>{header}</ModalHeader>
        <ModalBody>
          <p>
            {t("you-have-a-match")} {matched.length} {t("out-of")} {clientAreas}{" "}
            {t(name).toLowerCase()}.&nbsp;
            {coach.firstName} {t("also-expertise")} {coachArr.length}{" "}
            {t("additional")} {t(name).toLowerCase()}.
          </p>
          <p className="mt-4 font-medium">
            {t("matched")} {t(name).toLowerCase()}
          </p>
          <div className="mt-4 flex flex-row gap-2 flex-wrap">
            {matched.map((item: string, index) => (
              <Badge
                key={`match_${index}`}
                label={item}
                size="md"
                round={true}
                color="bg-indigo-700"
              >
                <></>
              </Badge>
            ))}
          </div>

          <hr className="mt-4 border-indigo-800" />

          <p className="mt-4 font-medium">
            {t("your")} {t("other")} {t(name).toLowerCase()}
          </p>
          <div className="mt-4 flex flex-row gap-2 flex-wrap">
            {clientArr.map((item: string, index) => (
              <Badge
                key={`client_${index}`}
                label={item}
                size="md"
                round={true}
                className="border border-indigo-700"
              >
                <></>
              </Badge>
            ))}
          </div>

          <hr className="mt-4 border-indigo-800" />

          <p className="mt-4 font-medium">
            {coach.firstName}
            {lang === "sv" ? "" : "'"}s {t("other")} {t(name).toLowerCase()}
          </p>
          <div className="mt-4 flex flex-row gap-2 flex-wrap">
            {coachArr.map((item: string, index) => (
              <Badge
                key={`coach_${index}`}
                label={item}
                size="md"
                round={true}
                className="border border-indigo-700"
              >
                <></>
              </Badge>
            ))}
          </div>
        </ModalBody>
      </Modal>
    );
  };

  return (
    <div className="main-content">
      {showMore && showMoreData()}

      {/* Heading */}
      <div>
        {/* Profile pic */}
        <div className="flex justify-center">
          {coach.picture ? (
            <img className="w-60 h-60 object-contain rounded-full" src={coach.picture} alt="pic" />
          ) : (
            <AccountIcon className="w-60 h-60 object-contain rounded-full" alt="pic" />
          )}
        </div>

        {/* Intro */}
        <div className="flex flex-col items-center justify-between">
          <h1 className="mt-4 z-heading2">
            {coach.firstName} {coach.lastName}
          </h1>
          <p className="text-base text-gray-400 whitespace-pre-line text-center">
            {coach.profile.intro}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {/* About me */}
        <div>
          <p className="font-medium">{t("about-me")}</p>
          <p className="text-gray-400 mt-2">{coach.profile.aboutMe}</p>
        </div>

        <hr className="border-indigo-800" />

        {/* Coaching areas */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-row w-full justify-between">
            <p className="font-medium">{t("coaching-areas")}</p>
            <p
              className="z-title4 cursor-pointer"
              onClick={() => setShowMore("areas")}
            >
              {t("see-more")}
            </p>
          </div>
          <p className="text-gray-400 whitespace-pre-line">
            {coach.profile.coachingAreasDescription}
          </p>
          <div className="flex flex-row gap-2 flex-wrap">
            {coach.profile.coachingAreas
              .filter((area) => clientProfile.coachingAreas.includes(area.key))
              .map((area) => (
                <Badge
                  key={`coaching-areas-${coach.id}-${area.key}`}
                  label={t(area.key)}
                  size="md"
                  round={true}
                  color="bg-indigo-700"
                >
                  <></>
                </Badge>
              ))}
          </div>
        </div>

        <hr className="border-indigo-800" />

        {/* Coach Industries */}

        <div className="flex flex-col gap-2">
          <div className="flex flex-row w-full justify-between">
            <p className="font-medium">{t("industries")}</p>
            <p
              className="z-title4 cursor-pointer"
              onClick={() => setShowMore("client_industries")}
            >
              {t("see-more")}
            </p>
          </div>
          <p className="text-gray-400 whitespace-pre-line">
            {coach.profile.industryExperienceDescription}
          </p>
          <div className="flex flex-row gap-2 flex-wrap">
            {coach.profile.clientIndustries
              .filter((area) =>
                clientProfile.coachIndustryExperiences.includes(area)
              )
              .map((area) => (
                <Badge
                  key={`industry-${area}`}
                  label={t(area)}
                  size="md"
                  round={true}
                  color="bg-indigo-700"
                >
                  <></>
                </Badge>
              ))}
          </div>
        </div>

        <hr className="border-indigo-800" />

        {/* Client Industries */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-row w-full justify-between">
            <p className="font-medium">{t("coach-industry-experience")}</p>
            <p
              className="z-title4 cursor-pointer"
              onClick={() => setShowMore("coach_industries")}
            >
              {t("see-more")}
            </p>
          </div>
          <p className="text-gray-400 whitespace-pre-line">
            {coach.profile.clientIndustriesDescription}
          </p>
          <div className="flex flex-row gap-2 flex-wrap">
            {coach.profile.industries
              .filter((area) =>
                clientProfile.coachIndustryExperiences.includes(area)
              )
              .map((area) => (
                <Badge
                  key={`c-i-${area}`}
                  label={t(area)}
                  size="md"
                  round={true}
                  color="bg-indigo-700"
                >
                  <></>
                </Badge>
              ))}
          </div>
        </div>

        <hr className="border-indigo-800" />

        {/* Philosophy */}
        <div className="">
          <p className="font-medium">{t("philosophy")}</p>
          <p className="mt-2 text-gray-400 whitespace-pre-line">
            {coach.profile.philosophy}
          </p>
        </div>

        <hr className="border-indigo-800" />

        <div className="flex flex-col gap-2">
          <div className="flex flex-row w-full justify-between">
            <p className="font-medium">{t("languages")}</p>
            <p
              className="z-title4 cursor-pointer"
              onClick={() => setShowMore("languages")}
            >
              {t("see-more")}
            </p>
          </div>
          <div className="flex flex-row gap-2 flex-wrap">
            {coach.profile.languages
              .filter((area) =>
                clientProfile.languagesProfessional.includes(area)
              )
              .map((area) => (
                <Badge
                  key={`lang-${area}`}
                  label={t("language_" + area)}
                  size="md"
                  round={true}
                  color="bg-indigo-700"
                >
                  <></>
                </Badge>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachMatching;
